import { A, useParams } from "@solidjs/router";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader } from "../../lib/async.ts";
import { formatMoment } from "../../lib/format.ts";
import { ok } from "../../api/result.ts";
import { asOfHref, describeChange, newestFirst } from "./history.ts";
import styles from "./admin.module.scss";

export type HistoryViewProps = {
  entityType: string;
  id: string;
};

/**
 * The audit trail of one entity: every recorded change, newest first, each with
 * a jump into the report builder as of that moment. The jump carries the event's
 * timestamp, which the builder replays through the API's as-of support.
 */
export const HistoryView = (props: HistoryViewProps) => {
  const api = useApi();
  const i18n = useI18n();

  const events = createLoader(async () => {
    const result = await api.history(props.entityType, props.id, { limit: 200 });
    return result.ok ? ok(newestFirst(result.value)) : result;
  });

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().history.title}</h1>
        <p class={styles.mono}>
          {props.entityType} · {props.id}
        </p>
      </div>

      <Panel title={i18n.t().history.title}>
        <Async value={events.value()} onRetry={events.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(event) => event.id}
              emptyLabel={i18n.t().history.noEvents}
              columns={[
                {
                  key: "at",
                  header: i18n.t().history.at,
                  cell: (event) => formatMoment(event.at, i18n.locale()),
                },
                {
                  key: "change",
                  header: i18n.t().history.change,
                  cell: (event) => describeChange(i18n.t(), event),
                },
                {
                  key: "actor",
                  header: i18n.t().history.actor,
                  cell: (event) => event.actor,
                },
                {
                  key: "asOf",
                  header: i18n.t().common.actions,
                  cell: (event) => (
                    <A href={asOfHref(event.at)}>{i18n.t().history.asOfJump}</A>
                  ),
                },
              ]}
            />
          )}
        </Async>
      </Panel>
    </>
  );
};

/** The route: the entity kind and its identity come from the URL. */
export const HistoryPage = () => {
  const params = useParams<{ entityType: string; id: string }>();
  return <HistoryView entityType={params.entityType} id={params.id} />;
};
